import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format, subDays } from 'date-fns';
import { orderShareApi } from '../../services/api';
import NetworkHeroKPIs from './NetworkHeroKPIs';
import OrderShareTable from './OrderShareTable';
import PerformanceTabBar from './PerformanceTabBar';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';
import { formatCurrency } from '../../utils/formatters';

const PRESETS = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 12 months', days: 365 },
];

const TREND_OPTIONS = [6, 12, 18, 24];

const ORDER_SHARE_CARDS = [
  {
    key: 'est_total_orders',
    label: 'Est. webshop orders',
    color: '#1976d2',
    format: (v) => (v || 0).toLocaleString(),
    tooltip:
      'Estimated total webshop orders across all retailers, inferred from the span of their ' +
      'order-number counters. Includes orders we never see.',
  },
  {
    key: 'handoff_orders',
    label: 'HandOff orders',
    color: '#2e7d32',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Orders with an APPROVED HandOff commission. Declined items are excluded.',
  },
  {
    key: 'blended_share_pct',
    label: 'Blended share',
    color: '#9c27b0',
    format: (v) => (v == null ? '—' : `${v}%`),
    tooltip:
      'Network-wide HandOff orders as a share of estimated webshop orders. Weighted by volume, ' +
      'so large retailers dominate it.',
  },
  {
    key: 'handoff_net_revenue',
    label: 'HandOff net revenue',
    color: '#ed6c02',
    format: formatCurrency,
    tooltip: "HandOff's commission on approved items only. Gross (incl. declined) shown per row.",
  },
];

export default function OrderShareDashboard() {
  // Default is FULL HISTORY, unlike the sibling tabs. This tab is a structural estimate;
  // a 30-day default would put almost every row at 'medium' confidence on first load.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [trendMonths, setTrendMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await orderShareApi.getSummary({
        from_date: fromDate || undefined,
        to_date: toDate,
        trend_months: trendMonths,
      });
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load order share data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, trendMonths]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const applyPreset = (days) => {
    setFromDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setToDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const clearRange = () => {
    setFromDate('');
    setToDate(format(new Date(), 'yyyy-MM-dd'));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Network Performance
      </Typography>
      <PerformanceTabBar />

      <Alert severity="info" sx={{ mb: 2 }}>
        Estimates how much of each retailer&apos;s <strong>webshop</strong> volume comes from
        HandOff, by reading the span of their order-number counters. <strong>Internal only</strong>
        {' '}— never shown to retailers.
      </Alert>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 2 },
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
            helperText={fromDate ? ' ' : 'all history'}
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="All history"
            size="small"
            onClick={clearRange}
            variant={fromDate ? 'outlined' : 'filled'}
            color={fromDate ? 'default' : 'primary'}
          />
          {PRESETS.map((p) => (
            <Chip
              key={p.days}
              label={p.label}
              size="small"
              onClick={() => applyPreset(p.days)}
              variant={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'filled' : 'outlined'}
              color={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'primary' : 'default'}
            />
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">Trend</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={trendMonths}
            onChange={(e, v) => v && setTrendMonths(v)}
          >
            {TREND_OPTIONS.map((m) => (
              <ToggleButton key={m} value={m} sx={{ px: 1 }}>{m}m</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <NetworkHeroKPIs data={data?.network} cards={ORDER_SHARE_CARDS} />
          <OrderShareTable
            retailers={data?.retailers}
            trendMonths={data?.trend_window_months || trendMonths}
          />
        </Stack>
      )}
    </Box>
  );
}
